// Extension API service for plan features and feature validation
// Helper function to get auth token (copied from api.js)
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Dynamic extension configuration cache
let extensionConfigCache = null;
let lastConfigUpdate = null;
const CONFIG_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper function to fetch extension configuration from backend
const fetchExtensionConfig = async () => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch('/api/extensions/config/api-config', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch extension config: ${response.status}`);
    }

    const data = await response.json();
    if (data.success && data.data.extensionConfig) {
      return data.data.extensionConfig;
    } else {
      throw new Error('Invalid extension config response');
    }
  } catch (error) {
    console.error('Error fetching extension config:', error);
    // Fallback to hardcoded config if API fails
    return {
      '66d878bc54398e0a6c23b84c': { apiUrl: 'https://api.featherconnector.io', name: 'Feather Connector v2.0.4' },
      '68582ff89bf2da89391c8631': { apiUrl: 'https://api.featherconnector.io', name: 'Feather Connector v2.0.5' },
      '68727db36de83f25cc8513a6': { apiUrl: 'https://api.featherconnector.io', name: 'Genius Connector v2.0.5' }
    };
  }
};

// Helper function to get extension configuration (with caching)
const getExtensionConfig = async () => {
  const now = Date.now();
  
  // Return cached config if still valid
  if (extensionConfigCache && lastConfigUpdate && (now - lastConfigUpdate) < CONFIG_CACHE_DURATION) {
    return extensionConfigCache;
  }

  // Fetch fresh config
  extensionConfigCache = await fetchExtensionConfig();
  lastConfigUpdate = now;
  
  return extensionConfigCache;
};

// Helper function to make authenticated requests to extension APIs
const makeExtensionRequest = async (extensionId, endpoint, options = {}) => {
  const extensionConfig = await getExtensionConfig();
  const extension = extensionConfig[extensionId];
  
  if (!extension || !extension.apiUrl) {
    throw new Error(`Extension API not configured for extension ID: ${extensionId}`);
  }

  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const url = `${extension.apiUrl}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  });

  if (!response.ok) {
    throw new Error(`Extension API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Get plan features for a specific extension
export const getExtensionPlanFeatures = async (extensionId) => {
  try {
    const response = await makeExtensionRequest(extensionId, '/api/extension/plan-features');
    return response;
  } catch (error) {
    console.error('Error fetching extension plan features:', error);
    throw error;
  }
};

// Validate feature access for a specific extension
export const validateExtensionFeature = async (extensionId, feature, plan) => {
  try {
    const response = await makeExtensionRequest(extensionId, '/api/extension/validate-feature', {
      method: 'POST',
      body: JSON.stringify({ feature, plan })
    });
    return response;
  } catch (error) {
    console.error('Error validating extension feature:', error);
    throw error;
  }
};

// Get plan name for a specific extension
export const getExtensionPlanName = async (extensionId, plan) => {
  try {
    const response = await getExtensionPlanFeatures(extensionId);
    if (response.success && response.data.planNames) {
      return response.data.planNames[plan] || 'Unknown';
    }
    return 'Unknown';
  } catch (error) {
    console.error('Error getting extension plan name:', error);
    return 'Unknown';
  }
};

// Get plan features for a specific extension
export const getExtensionPlanFeaturesList = async (extensionId, plan) => {
  try {
    const response = await getExtensionPlanFeatures(extensionId);
    if (response.success && response.data.planFeatures) {
      return response.data.planFeatures[plan] || [];
    }
    return [];
  } catch (error) {
    console.error('Error getting extension plan features:', error);
    return [];
  }
};

// Check if user can access a specific feature in an extension
export const canAccessExtensionFeature = async (extensionId, feature, plan) => {
  try {
    const response = await validateExtensionFeature(extensionId, feature, plan);
    return response.success && response.data.hasAccess;
  } catch (error) {
    console.error('Error checking extension feature access:', error);
    return false;
  }
}; 